import React, { useContext, useRef } from 'react';

type GestureInteraction =
    | 'none'
    | 'drag slide'
    | 'pinch zoom'
    | 'double tap zoom'
    | 'pan zoom';

type GalleryGestureLock = {
    isZoomed: boolean;
    interaction: GestureInteraction;
};

const GalleryGestureLockContext = React.createContext<
    | {
          lock: (interaction: GestureInteraction) => boolean;
          unlock: (interaction: GestureInteraction) => boolean;
          getActiveInteraction: () => GestureInteraction;
          setZoomed: (isZoomed: boolean) => void;
          isZoomed: () => boolean;
      }
    | undefined
>(undefined);

const GalleryGestureLockProvider: React.FC = ({ children }) => {
    const { current: state } = useRef<GalleryGestureLock>({
        isZoomed: false,
        interaction: 'none'
    });

    const context = {
        lock: (interaction: GestureInteraction) => {
            // Cannot lock if another interaction already has one
            if (state.interaction !== 'none') {
                return false;
            }

            switch (interaction) {
                case 'drag slide':
                    if (state.isZoomed) {
                        return false;
                    }
                    break;
                case 'pan zoom':
                    if (!state.isZoomed) {
                        return false;
                    }
                    break;
            }

            // No conditions matched above to prevent the lock
            state.interaction = interaction;
            return true;
        },

        unlock: (interaction: GestureInteraction) => {
            if (state.interaction !== interaction) {
                return false;
            }
            state.interaction = 'none';
            return true;
        },

        getActiveInteraction: () => state.interaction,

        setZoomed: (isZoomed: boolean) => (state.isZoomed = isZoomed),
        isZoomed: () => state.isZoomed
    };

    return (
        <GalleryGestureLockContext.Provider value={context}>
            {children}
        </GalleryGestureLockContext.Provider>
    );
};

const useGalleryGestureLock = () => {
    const state = useContext(GalleryGestureLockContext);

    if (state === undefined) {
        throw new Error(
            'Component must be descendent of GalleryGestureLockProvider'
        );
    }

    return state;
};

export { useGalleryGestureLock, GalleryGestureLockProvider };
